import { useCallback, useEffect, useRef, useState } from 'react';

import { DEFAULT_TEXT_BACKGROUND, DEFAULT_TEXT_OUTLINE, DEFAULT_TEXT_SHADOW } from '../config';
import { ELEMENT_TYPES } from '../core/documentSchema';
import { applyStyleToBlock, applyStyleToRange, getBlockStyle, getRangeStyle } from '../elements/text/textRuns';

const sharedValue = (values) => (values.every((value) => value === values[0]) ? values[0] : null);

const mergeValues = (objects) =>
  [...new Set(objects.flatMap((object) => Object.keys(object)))].reduce((acc, key) => {
    acc[key] = sharedValue(objects.map((object) => object[key]));
    return acc;
  }, {});

const mergeEffect = (elements, key, defaults) =>
  mergeValues(elements.map((element) => ({ ...defaults, ...(element[key] || {}) })));

const mergeTextElements = (elements) => ({
  id: null,
  type: ELEMENT_TYPES.TEXT,
  locked: elements.some((element) => element.locked),
  fontSize: Math.max(...elements.map((element) => getBlockStyle(element).fontSize)),
  align: sharedValue(elements.map((element) => element.align)),
  lineHeight: sharedValue(elements.map((element) => element.lineHeight)),
  letterSpacing: sharedValue(elements.map((element) => element.letterSpacing)),
  shadow: mergeEffect(elements, 'shadow', DEFAULT_TEXT_SHADOW),
  outline: mergeEffect(elements, 'outline', DEFAULT_TEXT_OUTLINE),
  background: mergeEffect(elements, 'background', DEFAULT_TEXT_BACKGROUND),
});

const resolve = (value, arg) => (typeof value === 'function' ? value(arg) : value);

const useTextEditing = ({ editor, selectedElement, selectedElements = [] }) => {
  const [editingId, setEditingId] = useState(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const editorRef = useRef(editor);
  editorRef.current = editor;

  const isGroup =
    selectedElements.length > 1 && selectedElements.every((element) => element.type === ELEMENT_TYPES.TEXT);
  const groupElements = isGroup ? selectedElements : null;
  const singleText =
    selectedElements.length <= 1 && selectedElement?.type === ELEMENT_TYPES.TEXT ? selectedElement : null;
  const textElement = groupElements ? mergeTextElements(groupElements) : singleText;
  const textIds = groupElements ? groupElements.map((element) => element.id) : singleText ? [singleText.id] : [];
  const isEditing = Boolean(singleText && editingId === singleText.id);
  const hasRange = isEditing && selection.end > selection.start;

  const startEditing = useCallback((id) => {
    editorRef.current.selectElement(id);
    setEditingId(id);
  }, []);

  const editingIdRef = useRef(editingId);
  editingIdRef.current = editingId;

  const finishEditing = useCallback(() => {
    const id = editingIdRef.current;
    if (!id) {
      return;
    }
    editingIdRef.current = null;
    setEditingId(null);
    const store = editorRef.current;
    store.endGesture();
    const element = store.elements.find((item) => item.id === id);
    if (element && !element.text.trim()) {
      store.deleteElement(id);
    }
  }, []);

  useEffect(() => {
    if (editingId && editingId !== editor.selectedId) {
      setEditingId(null);
      editor.endGesture();
    }
  }, [editingId, editor]);

  const updateRuns = useCallback(
    (runs) => {
      if (!editingId) {
        return;
      }
      editor.change(editingId, { runs }, { live: true });
    },
    [editingId, editor],
  );

  const update = (changes, options) => {
    if (groupElements) {
      editor.changeElements(
        groupElements.map((element) => ({ id: element.id, changes: resolve(changes, element) })),
        options,
      );
    } else if (singleText) {
      editor.change(singleText.id, resolve(changes, singleText), options);
    }
  };

  const currentStyle = groupElements
    ? mergeValues(groupElements.map((element) => getBlockStyle(element)))
    : singleText
    ? hasRange
      ? getRangeStyle(singleText, selection.start, selection.end)
      : getBlockStyle(singleText)
    : null;

  const applyStyle = (patch, options) => {
    if (groupElements) {
      update((element) => applyStyleToBlock(element, resolve(patch, getBlockStyle(element))), options);
    } else if (singleText) {
      const resolved = resolve(patch, currentStyle);
      if (hasRange) {
        update({ runs: applyStyleToRange(singleText, selection.start, selection.end, resolved) }, options);
      } else {
        update(applyStyleToBlock(singleText, resolved), options);
      }
    }
  };

  const toggleStyle = (key) => applyStyle({ [key]: !currentStyle?.[key] });

  const isMixed = (value) => isGroup && value == null;

  return {
    editingId,
    selection,
    setSelection,
    startEditing,
    finishEditing,
    updateRuns,
    textElement,
    textIds,
    isGroup,
    isMixed,
    hasRange,
    currentStyle,
    applyStyle,
    toggleStyle,
    updateBlock: update,
    endGesture: editor.endGesture,
  };
};

export default useTextEditing;
